/**
 * agentMarketplace.js — Pre-built Agent Marketplace
 * ===================================================
 * Comprehensive library of pre-built AI agents organized by SOC role,
 * skill level (tier), and responsibility domain.
 *
 * Agent Lifecycle: Install → Configure → Validate → Promote → Active → Monitor
 *
 * v1.0.0 — Security Engineer agents (Tier 1–3)
 */

/* ─── AGENT ROLE CATEGORIES ─────────────────────────────────────────── */

export const AGENT_ROLE_CATEGORIES = [
  {
    id: 'soc-analyst',
    name: 'SOC Analyst',
    icon: '🔍',
    color: '#0d9488',
    description: 'Tier 1 — First-line alert triage, log review, and escalation',
  },
  {
    id: 'secops-engineer',
    name: 'Security Engineer',
    icon: '🛡️',
    color: '#8b5cf6',
    description: 'Tier 2 — Incident response, threat hunting, and security tooling',
  },
  {
    id: 'senior-secops',
    name: 'Senior Security / Architect',
    icon: '🧠',
    color: '#ec4899',
    description: 'Tier 3 — Advanced threat analysis, forensics, and security architecture',
  },
  {
    id: 'vuln-management',
    name: 'Vulnerability Management',
    icon: '🎯',
    color: '#d97706',
    description: 'Scanning, prioritization, patching, and remediation tracking',
  },
  {
    id: 'cloud-security',
    name: 'Cloud Security',
    icon: '☁️',
    color: '#06b6d4',
    description: 'Cloud posture management, misconfig detection, and cloud IR',
  },
  {
    id: 'compliance-audit',
    name: 'Compliance & Audit',
    icon: '📋',
    color: '#78716c',
    description: 'Framework adherence, evidence collection, and audit readiness',
  },
  {
    id: 'identity-security',
    name: 'Identity & Access Security',
    icon: '🔑',
    color: '#f97316',
    description: 'IAM monitoring, privilege escalation detection, and access review',
  },
  {
    id: 'network-security',
    name: 'Network Security',
    icon: '🌐',
    color: '#6366f1',
    description: 'Network monitoring, lateral movement detection, and segmentation',
  },
];

/* ─── CONNECTOR ALTERNATIVES MAP ────────────────────────────────────── */
// Maps each connector to its interchangeable peers (same category).
// Used by the install UI to let customers swap tools within a category.

export const CONNECTOR_ALTERNATIVES = {
  // SIEM
  'splunk-v1':            { category: 'SIEM',                    peers: ['splunk-v1', 'sentinel-v1', 'elastic-security-v1'] },
  'sentinel-v1':          { category: 'SIEM',                    peers: ['splunk-v1', 'sentinel-v1', 'elastic-security-v1'] },
  'elastic-security-v1':  { category: 'SIEM',                    peers: ['splunk-v1', 'sentinel-v1', 'elastic-security-v1'] },
  // EDR
  'crowdstrike-v1':       { category: 'EDR',                     peers: ['crowdstrike-v1', 'sentinelone-v1', 'defender-endpoint-v1'] },
  'sentinelone-v1':       { category: 'EDR',                     peers: ['crowdstrike-v1', 'sentinelone-v1', 'defender-endpoint-v1'] },
  'defender-endpoint-v1': { category: 'EDR',                     peers: ['crowdstrike-v1', 'sentinelone-v1', 'defender-endpoint-v1'] },
  // Threat Intel
  'virustotal-v1':        { category: 'Threat Intel',            peers: ['virustotal-v1', 'misp-v1', 'recorded-future-v1'] },
  'misp-v1':              { category: 'Threat Intel',            peers: ['virustotal-v1', 'misp-v1', 'recorded-future-v1'] },
  'recorded-future-v1':   { category: 'Threat Intel',            peers: ['virustotal-v1', 'misp-v1', 'recorded-future-v1'] },
  // IAM
  'entra-id-v1':          { category: 'Identity & Access',       peers: ['entra-id-v1', 'okta-v1'] },
  'okta-v1':              { category: 'Identity & Access',       peers: ['entra-id-v1', 'okta-v1'] },
  // Vuln Mgmt
  'tenable-v1':           { category: 'Vulnerability Management', peers: ['tenable-v1', 'qualys-v1'] },
  'qualys-v1':            { category: 'Vulnerability Management', peers: ['tenable-v1', 'qualys-v1'] },
  // Network Security
  'palo-alto-ngfw-v1':    { category: 'Network Security',        peers: ['palo-alto-ngfw-v1'] },
  // Email Security
  'defender-o365-v1':     { category: 'Email Security',          peers: ['defender-o365-v1', 'proofpoint-v1'] },
  'proofpoint-v1':        { category: 'Email Security',          peers: ['defender-o365-v1', 'proofpoint-v1'] },
  // Cloud Security
  'aws-security-hub-v1':  { category: 'Cloud Security',          peers: ['aws-security-hub-v1', 'prisma-cloud-v1'] },
  'prisma-cloud-v1':      { category: 'Cloud Security',          peers: ['aws-security-hub-v1', 'prisma-cloud-v1'] },
  // CASB
  'netskope-v1':          { category: 'CASB / SASE',             peers: ['netskope-v1', 'zscaler-v1'] },
  'zscaler-v1':           { category: 'CASB / SASE',             peers: ['netskope-v1', 'zscaler-v1'] },
  // Ticketing
  'servicenow-v1':        { category: 'Ticketing',               peers: ['servicenow-v1', 'jira-v1', 'pagerduty-v1'] },
  'jira-v1':              { category: 'Ticketing',               peers: ['servicenow-v1', 'jira-v1', 'pagerduty-v1'] },
  'pagerduty-v1':         { category: 'Ticketing',               peers: ['servicenow-v1', 'jira-v1', 'pagerduty-v1'] },
  // Communication
  'slack-v1':             { category: 'Communication',           peers: ['slack-v1', 'teams-v1'] },
  'teams-v1':             { category: 'Communication',           peers: ['slack-v1', 'teams-v1'] },
  'email-smtp-v1':        { category: 'Communication',           peers: ['email-smtp-v1'] },
  // CRM / Business
  'salesforce-v1':        { category: 'CRM / Business',          peers: ['salesforce-v1'] },
};

/* ─── EXPERTISE LEVELS ──────────────────────────────────────────────── */

export const EXPERTISE_LEVELS = [
  { id: 'foundational', name: 'Foundational', color: '#0d9488', description: '0–2 years SOC experience' },
  { id: 'intermediate', name: 'Intermediate', color: '#8b5cf6', description: '2–5 years hands-on security operations' },
  { id: 'advanced', name: 'Advanced', color: '#ec4899', description: '5+ years, leads IR and hunts' },
  { id: 'expert', name: 'Expert', color: '#dc2626', description: 'Principal-level, architects defenses' },
];

/* ─── PRE-BUILT AGENTS ──────────────────────────────────────────────── */

export const MARKETPLACE_AGENTS = [

  /* ══════════════════════════════════════════════════════════════════
   *  TIER 1 — SOC ANALYST  (Foundational)
   * ══════════════════════════════════════════════════════════════════ */

  {
    id: 'alert-triage-agent',
    name: 'Alert Triage Analyst',
    version: '1.0.0',
    category: 'soc-analyst',
    expertise: 'foundational',
    tier: 'starter',
    status: 'stable',
    icon: '🚨',
    vendor: 'Agentlets',
    description:
      'First-line alert triage agent. Ingests SIEM alerts, deduplicates, enriches with threat intel, assigns severity scores, and routes to appropriate queues or escalates to Tier 2.',
    longDescription:
      'Designed for high-volume SOC environments, this agent automates the most repetitive Tier 1 tasks. It connects to your SIEM (Splunk, Elastic, or Sentinel), pulls new alerts on a configurable cadence, deduplicates identical or near-identical alerts within a time window, enriches IOCs against VirusTotal and MISP, assigns a normalized severity score (0–100), and either auto-closes false positives or routes true positives to the right Tier 2 queue. Includes built-in MITRE ATT&CK tactic tagging.',
    tags: ['siem', 'triage', 'alerts', 'dedup', 'enrichment', 'mitre'],
    linkedSkills: [
      { id: 'query-siem-alerts', name: 'Query SIEM Alerts', type: 'connector_skill', connector: 'splunk-v1' },
      { id: 'enrich-ioc-vt', name: 'Enrich IOC (VirusTotal)', type: 'connector_skill', connector: 'virustotal-v1' },
      { id: 'enrich-ioc-misp', name: 'Enrich IOC (MISP)', type: 'connector_skill', connector: 'misp-v1' },
      { id: 'deduplicate-alerts', name: 'Deduplicate & Correlate Alerts', type: 'standard' },
      { id: 'assign-severity-score', name: 'Assign Severity Score (0–100)', type: 'standard' },
      { id: 'create-ticket', name: 'Create Triage Ticket', type: 'connector_skill', connector: 'servicenow-v1' },
      { id: 'notify-slack-triage', name: 'Notify SOC Channel', type: 'connector_skill', connector: 'slack-v1' },
    ],
    linkedWorkflows: [
      { id: 'wf-alert-triage-pipeline', name: 'Alert Triage Pipeline', description: 'Ingest → Dedup → Enrich → Score → Route' },
    ],
    linkedConnectors: ['splunk-v1', 'virustotal-v1', 'misp-v1', 'servicenow-v1', 'slack-v1'],
    policyConfig: {
      maxAutoClosePerHour: 200,
      autoCloseThreshold: 15,
      escalationThreshold: 65,
      requireHumanReviewAbove: 80,
      allowedActions: ['close-false-positive', 'escalate', 'create-ticket', 'notify'],
      blockedActions: ['isolate-host', 'disable-account', 'block-ip'],
    },
    guardrails: [
      { id: 'gr-no-auto-close-critical', severity: 'critical', rule: 'Never auto-close alerts with severity score above 80 — always escalate to human analyst' },
      { id: 'gr-enrich-before-close', severity: 'high', rule: 'Must enrich at least 1 IOC before marking alert as false positive' },
      { id: 'gr-rate-limit-close', severity: 'medium', rule: 'Max 200 auto-closures per hour — pause and alert SOC lead if exceeded' },
    ],
    evals: [
      { name: 'True Positive Detection Rate', type: 'functional', description: 'Validates agent correctly identifies known-bad IOCs from test feed (target: >95%)' },
      { name: 'False Positive Rate', type: 'functional', description: 'Measures incorrect auto-closures against labeled test set (target: <3%)' },
      { name: 'Guardrail: No Critical Auto-Close', type: 'guardrail', description: 'Injects critical-severity alert and verifies agent escalates rather than closes' },
    ],
    estimatedSetupMinutes: 15,
    requiredConnectors: ['splunk-v1 OR sentinel-v1 OR elastic-security-v1', 'virustotal-v1', 'servicenow-v1 OR jira-v1'],
  },

  {
    id: 'phishing-analysis-agent',
    name: 'Phishing Email Analyzer',
    version: '1.0.0',
    category: 'soc-analyst',
    expertise: 'foundational',
    tier: 'starter',
    status: 'stable',
    icon: '📧',
    vendor: 'Agentlets',
    description:
      'Automated phishing email analysis. Extracts URLs, attachments, and sender metadata; checks against known-bad lists; determines verdict (malicious / suspicious / clean).',
    longDescription:
      'Handles the full lifecycle of reported phishing emails. Parses EML/MSG files, extracts all URLs and attachment hashes, checks URLs against VirusTotal and URLhaus, verifies sender SPF/DKIM/DMARC alignment, scores the email on a phishing probability scale, and generates a structured analysis report. Can auto-quarantine mailboxes, notify the reporting user, and create a tracking ticket.',
    tags: ['phishing', 'email', 'url-analysis', 'attachment', 'spf', 'dkim'],
    linkedSkills: [
      { id: 'parse-email-headers', name: 'Parse Email Headers & Body', type: 'standard' },
      { id: 'extract-urls-attachments', name: 'Extract URLs & Attachments', type: 'standard' },
      { id: 'check-url-reputation', name: 'Check URL Reputation', type: 'connector_skill', connector: 'virustotal-v1' },
      { id: 'check-attachment-hash', name: 'Check Attachment Hash', type: 'connector_skill', connector: 'virustotal-v1' },
      { id: 'verify-spf-dkim', name: 'Verify SPF/DKIM/DMARC', type: 'standard' },
      { id: 'generate-verdict', name: 'Generate Phishing Verdict', type: 'standard' },
      { id: 'create-phish-ticket', name: 'Create Phishing Ticket', type: 'connector_skill', connector: 'servicenow-v1' },
      { id: 'notify-user-result', name: 'Notify Reporting User', type: 'connector_skill', connector: 'email-smtp-v1' },
    ],
    linkedWorkflows: [
      { id: 'wf-phishing-pipeline', name: 'Phishing Analysis Pipeline', description: 'Parse → Extract → Enrich → Score → Verdict → Respond' },
    ],
    linkedConnectors: ['virustotal-v1', 'defender-o365-v1', 'servicenow-v1', 'email-smtp-v1', 'slack-v1'],
    policyConfig: {
      autoQuarantineAboveScore: 90,
      requireHumanVerdictAbove: 70,
      allowedActions: ['quarantine-email', 'notify-user', 'create-ticket', 'block-sender'],
      blockedActions: ['delete-mailbox', 'disable-account'],
    },
    guardrails: [
      { id: 'gr-no-auto-delete', severity: 'critical', rule: 'Never auto-delete emails — only quarantine with human review' },
      { id: 'gr-verify-before-block', severity: 'high', rule: 'Must have at least 2 positive signals before blocking a sender domain' },
    ],
    evals: [
      { name: 'Known Phish Detection', type: 'functional', description: 'Tests against 50 known phishing samples — target >98% detection' },
      { name: 'Legitimate Email Preservation', type: 'negative', description: 'Tests against 50 legitimate emails — target 0 false quarantines' },
    ],
    estimatedSetupMinutes: 10,
    requiredConnectors: ['virustotal-v1', 'defender-o365-v1 OR proofpoint-v1'],
  },

  {
    id: 'log-health-monitor-agent',
    name: 'Log Source Health Monitor',
    version: '1.0.0',
    category: 'soc-analyst',
    expertise: 'foundational',
    tier: 'starter',
    status: 'stable',
    icon: '📊',
    vendor: 'Agentlets',
    description:
      'Monitors SIEM log source health. Detects silent log sources, ingestion lag, volume anomalies, and missing critical feeds. Alerts SOC before blind spots become security gaps.',
    longDescription:
      'Continuously monitors the health of all data sources feeding your SIEM. Tracks per-source event volume baselines, detects when a source goes silent or drops below expected thresholds, identifies ingestion lag that could delay alert correlation, and ensures critical feeds (EDR, firewall, DNS) are always flowing. Generates daily health reports and immediate alerts for critical source failures.',
    tags: ['log-management', 'siem-health', 'monitoring', 'data-sources', 'visibility'],
    linkedSkills: [
      { id: 'query-source-stats', name: 'Query Log Source Statistics', type: 'connector_skill', connector: 'splunk-v1' },
      { id: 'compute-volume-baselines', name: 'Compute Volume Baselines', type: 'standard' },
      { id: 'detect-silent-sources', name: 'Detect Silent Sources', type: 'standard' },
      { id: 'detect-volume-anomaly', name: 'Detect Volume Anomalies', type: 'standard' },
      { id: 'generate-health-report', name: 'Generate Health Report', type: 'standard' },
      { id: 'alert-soc-source-down', name: 'Alert SOC: Source Down', type: 'connector_skill', connector: 'slack-v1' },
    ],
    linkedWorkflows: [
      { id: 'wf-log-health-check', name: 'Log Health Check', description: 'Query → Baseline → Detect Anomalies → Report → Alert' },
    ],
    linkedConnectors: ['splunk-v1', 'slack-v1', 'email-smtp-v1'],
    policyConfig: {
      checkIntervalMinutes: 15,
      silentThresholdMinutes: 30,
      volumeDropAlertPercent: 50,
      criticalSources: ['edr', 'firewall', 'dns', 'proxy', 'authentication'],
      allowedActions: ['alert', 'create-ticket', 'generate-report'],
      blockedActions: ['restart-service', 'modify-config'],
    },
    guardrails: [
      { id: 'gr-no-infra-changes', severity: 'critical', rule: 'Never attempt to restart or reconfigure log sources — only alert humans' },
      { id: 'gr-critical-immediate', severity: 'high', rule: 'Critical source failures (EDR, FW, DNS) must trigger immediate Slack + email alert' },
    ],
    evals: [
      { name: 'Silent Source Detection', type: 'functional', description: 'Simulates source going dark — verifies alert fires within 2 check cycles' },
      { name: 'Volume Drop Detection', type: 'functional', description: 'Injects 60% volume drop — verifies anomaly detected' },
    ],
    estimatedSetupMinutes: 10,
    requiredConnectors: ['splunk-v1 OR sentinel-v1 OR elastic-security-v1', 'slack-v1'],
  },

  /* ══════════════════════════════════════════════════════════════════
   *  TIER 2 — SECURITY ENGINEER  (Intermediate)
   * ══════════════════════════════════════════════════════════════════ */

  {
    id: 'incident-response-agent',
    name: 'Incident Response Coordinator',
    version: '1.0.0',
    category: 'secops-engineer',
    expertise: 'intermediate',
    tier: 'premium',
    status: 'stable',
    icon: '🚒',
    vendor: 'Agentlets',
    description:
      'Full-lifecycle incident response agent. Coordinates containment, eradication, and recovery across endpoints, network, and identity systems. Follows NIST 800-61 IR framework.',
    longDescription:
      'Orchestrates the complete incident response lifecycle following NIST SP 800-61 Rev 2. When triggered by a high-severity alert or human analyst, this agent: (1) gathers context from SIEM, EDR, and identity sources, (2) builds an incident timeline, (3) recommends and executes approved containment actions (host isolation, credential reset, firewall block), (4) tracks eradication steps, (5) monitors for re-compromise indicators during recovery, and (6) generates post-incident reports. All destructive actions require human approval through an approval queue.',
    tags: ['incident-response', 'containment', 'eradication', 'recovery', 'nist', 'timeline'],
    linkedSkills: [
      { id: 'gather-incident-context', name: 'Gather Incident Context', type: 'standard' },
      { id: 'build-timeline', name: 'Build Incident Timeline', type: 'standard' },
      { id: 'query-edr-host', name: 'Query EDR for Host Activity', type: 'connector_skill', connector: 'crowdstrike-v1' },
      { id: 'isolate-endpoint', name: 'Isolate Compromised Endpoint', type: 'connector_skill', connector: 'crowdstrike-v1' },
      { id: 'disable-user-account', name: 'Disable Compromised Account', type: 'connector_skill', connector: 'entra-id-v1' },
      { id: 'block-ip-firewall', name: 'Block IP at Firewall', type: 'connector_skill', connector: 'palo-alto-ngfw-v1' },
      { id: 'reset-credentials', name: 'Force Password Reset', type: 'connector_skill', connector: 'entra-id-v1' },
      { id: 'check-lateral-movement', name: 'Check Lateral Movement', type: 'connector_skill', connector: 'splunk-v1' },
      { id: 'create-ir-ticket', name: 'Create IR Ticket', type: 'connector_skill', connector: 'servicenow-v1' },
      { id: 'post-ir-summary', name: 'Post IR Summary to War Room', type: 'connector_skill', connector: 'slack-v1' },
      { id: 'generate-postmortem', name: 'Generate Post-Incident Report', type: 'standard' },
    ],
    linkedWorkflows: [
      { id: 'wf-ir-containment', name: 'Containment Workflow', description: 'Isolate Host → Block IPs → Disable Accounts → Verify' },
      { id: 'wf-ir-investigation', name: 'Investigation Workflow', description: 'Collect Evidence → Build Timeline → Identify Root Cause' },
      { id: 'wf-ir-recovery', name: 'Recovery Workflow', description: 'Remediate → Restore → Validate → Close' },
    ],
    linkedConnectors: ['splunk-v1', 'crowdstrike-v1', 'entra-id-v1', 'palo-alto-ngfw-v1', 'servicenow-v1', 'slack-v1'],
    policyConfig: {
      approvalRequired: ['isolate-endpoint', 'disable-user-account', 'block-ip-firewall', 'reset-credentials'],
      autoApproved: ['gather-incident-context', 'build-timeline', 'query-edr-host', 'check-lateral-movement', 'create-ir-ticket'],
      maxContainmentActionsPerIncident: 20,
      requireManagerApprovalForVIP: true,
      vipGroupIds: [],
      allowedActions: ['isolate', 'block-ip', 'disable-account', 'reset-password', 'create-ticket', 'notify'],
      blockedActions: ['wipe-endpoint', 'delete-account', 'modify-firewall-policy'],
    },
    guardrails: [
      { id: 'gr-no-auto-isolate', severity: 'critical', rule: 'Host isolation requires explicit human approval before execution' },
      { id: 'gr-no-account-delete', severity: 'critical', rule: 'Never delete user accounts — only disable them for containment' },
      { id: 'gr-vip-escalation', severity: 'high', rule: 'Actions on VIP/executive accounts must have manager + CISO approval' },
      { id: 'gr-document-everything', severity: 'medium', rule: 'Every containment action must be logged in the IR ticket before execution' },
    ],
    evals: [
      { name: 'Containment Workflow Test', type: 'functional', description: 'Simulates compromised host — verifies correct isolation + timeline build sequence' },
      { name: 'Approval Gate Test', type: 'guardrail', description: 'Attempts auto-isolation without approval — verifies rejection' },
      { name: 'VIP Escalation Test', type: 'guardrail', description: 'Tests containment on VIP account — verifies escalation chain triggered' },
      { name: 'Post-Incident Report Quality', type: 'functional', description: 'Validates report includes all NIST 800-61 required sections' },
    ],
    estimatedSetupMinutes: 30,
    requiredConnectors: ['splunk-v1 OR sentinel-v1', 'crowdstrike-v1 OR sentinelone-v1', 'entra-id-v1 OR okta-v1', 'servicenow-v1'],
  },

  {
    id: 'threat-hunt-agent',
    name: 'Proactive Threat Hunter',
    version: '1.0.0',
    category: 'secops-engineer',
    expertise: 'intermediate',
    tier: 'premium',
    status: 'stable',
    icon: '🎯',
    vendor: 'Agentlets',
    description:
      'Hypothesis-driven threat hunting agent. Generates hunt hypotheses from latest threat intel, constructs SIEM queries, analyzes results for anomalies, and documents findings.',
    longDescription:
      'Implements a structured threat hunting methodology. Sources latest threat intelligence from MISP, Recorded Future, and MITRE ATT&CK to generate hunt hypotheses. For each hypothesis, constructs targeted SIEM queries (SPL, KQL, or Lucene), executes them across configurable time ranges, analyzes results using statistical baselining and anomaly detection, and produces structured hunt reports. Integrates with your ticketing system to track hunts as campaigns and feed confirmed findings back into detection engineering.',
    tags: ['threat-hunting', 'hypothesis', 'mitre-attack', 'anomaly-detection', 'campaign'],
    linkedSkills: [
      { id: 'fetch-threat-intel', name: 'Fetch Latest Threat Intel', type: 'connector_skill', connector: 'recorded-future-v1' },
      { id: 'map-mitre-techniques', name: 'Map to MITRE ATT&CK', type: 'standard' },
      { id: 'generate-hunt-hypothesis', name: 'Generate Hunt Hypothesis', type: 'standard' },
      { id: 'construct-hunt-queries', name: 'Construct Hunt Queries', type: 'standard' },
      { id: 'execute-siem-search', name: 'Execute SIEM Search', type: 'connector_skill', connector: 'splunk-v1' },
      { id: 'baseline-analysis', name: 'Statistical Baseline Analysis', type: 'standard' },
      { id: 'generate-hunt-report', name: 'Generate Hunt Report', type: 'standard' },
      { id: 'create-detection-rule', name: 'Create Detection Rule (Draft)', type: 'standard' },
    ],
    linkedWorkflows: [
      { id: 'wf-threat-hunt-cycle', name: 'Threat Hunt Cycle', description: 'Intel Gathering → Hypothesis → Query → Analyze → Report' },
    ],
    linkedConnectors: ['splunk-v1', 'recorded-future-v1', 'misp-v1', 'servicenow-v1'],
    policyConfig: {
      maxQueryTimeRangeDays: 90,
      maxConcurrentHunts: 3,
      requireReviewBeforeDetectionDeploy: true,
      allowedActions: ['search-siem', 'fetch-intel', 'generate-report', 'draft-detection'],
      blockedActions: ['deploy-detection-rule', 'modify-siem-config', 'delete-logs'],
    },
    guardrails: [
      { id: 'gr-no-deploy-rules', severity: 'critical', rule: 'Never auto-deploy detection rules — only draft for human review' },
      { id: 'gr-query-limits', severity: 'high', rule: 'SIEM queries must be time-bounded and resource-capped to prevent performance impact' },
      { id: 'gr-document-hypothesis', severity: 'medium', rule: 'Every hunt must have a documented hypothesis before query execution' },
    ],
    evals: [
      { name: 'Hunt Hypothesis Quality', type: 'functional', description: 'Validates hypotheses are MITRE-aligned and testable with available data sources' },
      { name: 'Query Safety Test', type: 'security', description: 'Verifies generated queries include time bounds and do not use unbounded wildcards' },
    ],
    estimatedSetupMinutes: 25,
    requiredConnectors: ['splunk-v1 OR sentinel-v1 OR elastic-security-v1', 'recorded-future-v1 OR misp-v1'],
  },

  {
    id: 'edr-response-agent',
    name: 'Endpoint Detection & Response Agent',
    version: '1.0.0',
    category: 'secops-engineer',
    expertise: 'intermediate',
    tier: 'premium',
    status: 'stable',
    icon: '💻',
    vendor: 'Agentlets',
    description:
      'EDR-focused agent for endpoint investigation and response. Queries endpoint telemetry, analyzes process trees, identifies malicious binaries, and coordinates host-level containment.',
    longDescription:
      'Deep endpoint investigation and response agent. When an EDR alert fires, this agent: (1) queries the full process tree and network connections for the suspicious process, (2) checks file hashes against VT and internal threat intel, (3) analyzes command-line arguments for known attack patterns (LOLBins, PowerShell obfuscation, etc.), (4) determines blast radius by identifying lateral movement indicators, (5) recommends containment actions (isolate host, kill process, quarantine file), and (6) creates a detailed investigation report. Supports CrowdStrike Falcon, SentinelOne, and Microsoft Defender for Endpoint.',
    tags: ['edr', 'endpoint', 'process-tree', 'malware', 'lolbins', 'containment'],
    linkedSkills: [
      { id: 'get-edr-detections', name: 'Get EDR Detections', type: 'connector_skill', connector: 'crowdstrike-v1' },
      { id: 'query-process-tree', name: 'Query Process Tree', type: 'connector_skill', connector: 'crowdstrike-v1' },
      { id: 'check-file-hash', name: 'Check File Hash (VT)', type: 'connector_skill', connector: 'virustotal-v1' },
      { id: 'analyze-cmdline', name: 'Analyze Command-Line Patterns', type: 'standard' },
      { id: 'identify-lolbins', name: 'Identify LOLBin Usage', type: 'standard' },
      { id: 'contain-host-edr', name: 'Contain Host via EDR', type: 'connector_skill', connector: 'crowdstrike-v1' },
      { id: 's1-initiate-scan', name: 'Initiate Full Disk Scan', type: 'connector_skill', connector: 'sentinelone-v1' },
      { id: 's1-disconnect-net', name: 'Disconnect from Network', type: 'connector_skill', connector: 'sentinelone-v1' },
      { id: 'generate-edr-report', name: 'Generate EDR Investigation Report', type: 'standard' },
    ],
    linkedWorkflows: [
      { id: 'wf-edr-investigation', name: 'EDR Investigation', description: 'Alert → Process Tree → Hash Check → Pattern Analysis → Contain → Report' },
    ],
    linkedConnectors: ['crowdstrike-v1', 'sentinelone-v1', 'virustotal-v1', 'servicenow-v1', 'slack-v1'],
    policyConfig: {
      approvalRequired: ['contain-host-edr', 's1-disconnect-net'],
      autoApproved: ['get-edr-detections', 'query-process-tree', 'check-file-hash', 'analyze-cmdline'],
      allowedActions: ['query', 'scan', 'isolate', 'create-ticket', 'report'],
      blockedActions: ['wipe-endpoint', 'uninstall-edr', 'modify-edr-policy'],
    },
    guardrails: [
      { id: 'gr-no-edr-policy-change', severity: 'critical', rule: 'Never modify EDR exclusions or detection policies' },
      { id: 'gr-isolate-approval', severity: 'critical', rule: 'Host isolation requires SOC Lead approval' },
      { id: 'gr-preserve-evidence', severity: 'high', rule: 'Never delete or overwrite endpoint artifacts during investigation' },
    ],
    evals: [
      { name: 'LOLBin Detection', type: 'functional', description: 'Tests against 20 LOLBin patterns — target 100% identification' },
      { name: 'Process Tree Analysis', type: 'functional', description: 'Validates correct parent-child relationship mapping' },
      { name: 'No Auto-Isolation', type: 'guardrail', description: 'Attempts auto-contain without approval — verifies block' },
    ],
    estimatedSetupMinutes: 20,
    requiredConnectors: ['crowdstrike-v1 OR sentinelone-v1 OR defender-endpoint-v1', 'virustotal-v1'],
  },

  {
    id: 'secops-automation-agent',
    name: 'Security Automation Engineer',
    version: '1.0.0',
    category: 'secops-engineer',
    expertise: 'intermediate',
    tier: 'premium',
    status: 'stable',
    icon: '⚙️',
    vendor: 'Agentlets',
    description:
      'Automates repetitive security operations tasks: IOC blocklist updates, detection rule tuning, firewall rule management, and cross-tool integration maintenance.',
    longDescription:
      'The automation backbone of your SOC. This agent handles tasks that consume analyst time without requiring deep investigative skill: (1) Syncs IOC blocklists from threat intel to firewall and EDR, (2) Tunes noisy detection rules by analyzing false-positive rates and adjusting thresholds, (3) Manages firewall rule lifecycle (request → review → implement → verify → expire), (4) Keeps cross-tool integrations healthy (tests syslog shipping, API connectivity, webhook delivery). All changes are logged, version-controlled, and require approval for production deployment.',
    tags: ['automation', 'blocklist', 'detection-tuning', 'firewall', 'integration', 'orchestration'],
    linkedSkills: [
      { id: 'sync-ioc-blocklist', name: 'Sync IOC Blocklist', type: 'workflow_skill' },
      { id: 'analyze-fp-rates', name: 'Analyze False Positive Rates', type: 'connector_skill', connector: 'splunk-v1' },
      { id: 'tune-detection-threshold', name: 'Tune Detection Threshold (Draft)', type: 'standard' },
      { id: 'manage-fw-rules', name: 'Manage Firewall Rules', type: 'connector_skill', connector: 'palo-alto-ngfw-v1' },
      { id: 'test-integration-health', name: 'Test Integration Health', type: 'standard' },
      { id: 'update-blocklist-edr', name: 'Update EDR Blocklist', type: 'connector_skill', connector: 'crowdstrike-v1' },
    ],
    linkedWorkflows: [
      { id: 'wf-ioc-sync', name: 'IOC Blocklist Sync', description: 'Fetch Intel → Deduplicate → Push to FW + EDR → Verify → Report' },
      { id: 'wf-detection-tuning', name: 'Detection Tuning Cycle', description: 'Analyze FP Rate → Generate Adjustment → Submit for Review' },
    ],
    linkedConnectors: ['splunk-v1', 'crowdstrike-v1', 'palo-alto-ngfw-v1', 'misp-v1', 'recorded-future-v1'],
    policyConfig: {
      approvalRequired: ['tune-detection-threshold', 'manage-fw-rules', 'update-blocklist-edr'],
      autoApproved: ['analyze-fp-rates', 'test-integration-health', 'sync-ioc-blocklist'],
      maxBlocklistEntriesPerSync: 500,
      allowedActions: ['sync-blocklist', 'analyze', 'draft-rule-change', 'test-integration', 'report'],
      blockedActions: ['deploy-rule-change', 'delete-rule', 'modify-production-config'],
    },
    guardrails: [
      { id: 'gr-no-prod-deploy', severity: 'critical', rule: 'Rule changes must be drafted only — never deployed to production without human approval' },
      { id: 'gr-blocklist-limits', severity: 'high', rule: 'Max 500 entries per blocklist sync to prevent tool overload' },
    ],
    evals: [
      { name: 'IOC Sync Accuracy', type: 'functional', description: 'Validates synced IOCs match source feed with no corruption' },
      { name: 'No Auto-Deploy', type: 'guardrail', description: 'Attempts auto-deploy of rule change — verifies block' },
    ],
    estimatedSetupMinutes: 25,
    requiredConnectors: ['splunk-v1 OR sentinel-v1', 'crowdstrike-v1 OR sentinelone-v1', 'palo-alto-ngfw-v1'],
  },

  /* ══════════════════════════════════════════════════════════════════
   *  TIER 3 — SENIOR / ADVANCED SECURITY  (Advanced + Expert)
   * ══════════════════════════════════════════════════════════════════ */

  {
    id: 'advanced-threat-analysis-agent',
    name: 'Advanced Threat Analyst',
    version: '1.0.0',
    category: 'senior-secops',
    expertise: 'advanced',
    tier: 'enterprise',
    status: 'stable',
    icon: '🔬',
    vendor: 'Agentlets',
    description:
      'Advanced threat analysis agent for complex, multi-stage attacks. Performs kill chain analysis, maps attacker TTPs to MITRE ATT&CK, correlates across data sources, and produces intelligence reports.',
    longDescription:
      'Designed for senior analysts investigating sophisticated threats. This agent: (1) Correlates signals across SIEM, EDR, network, identity, and cloud to reconstruct multi-stage attack chains, (2) Maps every observed technique to MITRE ATT&CK with confidence scores, (3) Performs diamond model analysis (adversary, infrastructure, capability, victim), (4) Identifies attack group attribution indicators, (5) Generates detailed threat intelligence reports suitable for executive briefing and peer sharing (STIX/TAXII compatible), (6) Produces detection gap analysis showing where your coverage is weak against the observed TTPs.',
    tags: ['advanced-threats', 'kill-chain', 'mitre-attack', 'diamond-model', 'attribution', 'stix'],
    linkedSkills: [
      { id: 'cross-source-correlation', name: 'Cross-Source Signal Correlation', type: 'standard' },
      { id: 'kill-chain-reconstruction', name: 'Kill Chain Reconstruction', type: 'standard' },
      { id: 'mitre-ttp-mapping', name: 'MITRE ATT&CK TTP Mapping', type: 'standard' },
      { id: 'diamond-model-analysis', name: 'Diamond Model Analysis', type: 'standard' },
      { id: 'query-siem-advanced', name: 'Advanced SIEM Correlation Queries', type: 'connector_skill', connector: 'splunk-v1' },
      { id: 'query-edr-deep', name: 'Deep EDR Telemetry Query', type: 'connector_skill', connector: 'crowdstrike-v1' },
      { id: 'query-network-flows', name: 'Network Flow Analysis', type: 'connector_skill', connector: 'palo-alto-ngfw-v1' },
      { id: 'query-cloud-trail', name: 'Cloud Activity Trail Analysis', type: 'connector_skill', connector: 'aws-security-hub-v1' },
      { id: 'generate-threat-intel-report', name: 'Generate Threat Intel Report (STIX)', type: 'standard' },
      { id: 'detection-gap-analysis', name: 'Detection Coverage Gap Analysis', type: 'standard' },
      { id: 'attribution-indicators', name: 'Attribution Indicator Analysis', type: 'standard' },
    ],
    linkedWorkflows: [
      { id: 'wf-advanced-investigation', name: 'Advanced Threat Investigation', description: 'Multi-source correlation → Kill chain → TTP mapping → Diamond model → Intel report' },
      { id: 'wf-gap-analysis', name: 'Detection Gap Analysis', description: 'Map observed TTPs → Check detection coverage → Identify gaps → Recommend rules' },
    ],
    linkedConnectors: ['splunk-v1', 'crowdstrike-v1', 'palo-alto-ngfw-v1', 'aws-security-hub-v1', 'recorded-future-v1', 'misp-v1'],
    policyConfig: {
      maxCorrelationTimeRangeDays: 180,
      requirePeerReviewForAttribution: true,
      stixSharingRequiresApproval: true,
      allowedActions: ['query-all-sources', 'correlate', 'analyze', 'generate-report', 'draft-detection'],
      blockedActions: ['share-externally', 'publish-attribution', 'modify-infrastructure'],
    },
    guardrails: [
      { id: 'gr-attribution-peer-review', severity: 'critical', rule: 'Threat group attribution must have peer review before any external sharing' },
      { id: 'gr-no-auto-share-stix', severity: 'critical', rule: 'STIX intelligence packages must not be auto-shared — requires CISO approval' },
      { id: 'gr-confidence-scoring', severity: 'high', rule: 'All TTP mappings must include confidence scores (low/medium/high) with supporting evidence' },
    ],
    evals: [
      { name: 'Multi-Stage Attack Reconstruction', type: 'functional', description: 'Simulates APT kill chain — validates complete reconstruction with >90% TTP coverage' },
      { name: 'Attribution Peer Review Gate', type: 'guardrail', description: 'Attempts auto-publish attribution — verifies block' },
      { name: 'STIX Report Compliance', type: 'functional', description: 'Validates generated STIX report conforms to STIX 2.1 schema' },
    ],
    estimatedSetupMinutes: 40,
    requiredConnectors: ['splunk-v1 OR sentinel-v1', 'crowdstrike-v1 OR sentinelone-v1', 'recorded-future-v1', 'palo-alto-ngfw-v1'],
  },

  {
    id: 'forensics-agent',
    name: 'Digital Forensics Investigator',
    version: '1.0.0',
    category: 'senior-secops',
    expertise: 'advanced',
    tier: 'enterprise',
    status: 'stable',
    icon: '🔎',
    vendor: 'Agentlets',
    description:
      'Digital forensics agent for deep-dive investigations. Analyzes memory dumps, file system artifacts, audit logs, registry hives, and binary samples. Maintains chain of custody for legal proceedings.',
    longDescription:
      'Enterprise forensics agent for when you need to go deep. Handles: (1) Memory dump analysis — identifies injected code, suspicious threads, and rootkit indicators, (2) File system artifact analysis — examines MFT entries, prefetch files, shimcache, amcache, (3) Registry forensics — startup entries, USB history, user activity, (4) Log-based forensics — reconstructs user sessions from Windows Event Logs, auth logs, proxy logs, (5) Binary sample analysis — static analysis of PE/ELF headers, string extraction, import table analysis, (6) Chain of custody management — hashes, timestamps, and audit trail for every artifact examined.',
    tags: ['forensics', 'memory-analysis', 'artifacts', 'chain-of-custody', 'malware', 'legal-hold'],
    linkedSkills: [
      { id: 'analyze-memory-dump', name: 'Analyze Memory Dump', type: 'standard' },
      { id: 'analyze-filesystem-artifacts', name: 'Analyze File System Artifacts', type: 'standard' },
      { id: 'analyze-registry', name: 'Registry Hive Analysis', type: 'standard' },
      { id: 'reconstruct-user-session', name: 'Reconstruct User Session', type: 'connector_skill', connector: 'splunk-v1' },
      { id: 'static-binary-analysis', name: 'Static Binary Analysis', type: 'standard' },
      { id: 'hash-verify-artifacts', name: 'Hash & Verify Artifacts', type: 'standard' },
      { id: 'check-binary-vt', name: 'Check Binary (VirusTotal)', type: 'connector_skill', connector: 'virustotal-v1' },
      { id: 'generate-forensics-report', name: 'Generate Forensics Report', type: 'standard' },
      { id: 'chain-of-custody-log', name: 'Chain of Custody Log', type: 'standard' },
    ],
    linkedWorkflows: [
      { id: 'wf-forensic-investigation', name: 'Forensic Investigation', description: 'Evidence Collection → Hash Verification → Analysis → Timeline → Report' },
    ],
    linkedConnectors: ['splunk-v1', 'crowdstrike-v1', 'virustotal-v1', 'servicenow-v1'],
    policyConfig: {
      preserveAllArtifacts: true,
      requireChainOfCustody: true,
      legalHoldMode: false,
      allowedActions: ['analyze', 'hash', 'generate-report', 'create-ticket'],
      blockedActions: ['modify-evidence', 'delete-artifacts', 'transfer-externally'],
    },
    guardrails: [
      { id: 'gr-no-evidence-modification', severity: 'critical', rule: 'Never modify, delete, or overwrite any forensic evidence or artifacts' },
      { id: 'gr-chain-of-custody', severity: 'critical', rule: 'Every artifact access must be logged with timestamp, hash, and analyst identity' },
      { id: 'gr-legal-hold-check', severity: 'high', rule: 'Check legal hold status before any artifact processing — held items require Legal approval' },
    ],
    evals: [
      { name: 'Evidence Integrity', type: 'security', description: 'Verifies hash consistency maintained throughout investigation workflow' },
      { name: 'Rootkit Indicator Detection', type: 'functional', description: 'Tests against known rootkit memory patterns — target >90% detection' },
      { name: 'Chain of Custody Compliance', type: 'guardrail', description: 'Validates complete audit trail for every artifact touched' },
    ],
    estimatedSetupMinutes: 35,
    requiredConnectors: ['splunk-v1 OR sentinel-v1', 'virustotal-v1', 'crowdstrike-v1 OR sentinelone-v1'],
  },

  {
    id: 'ransomware-response-agent',
    name: 'Ransomware Response Specialist',
    version: '1.0.0',
    category: 'senior-secops',
    expertise: 'expert',
    tier: 'enterprise',
    status: 'stable',
    icon: '🛑',
    vendor: 'Agentlets',
    description:
      'Specialized ransomware incident agent. Identifies ransomware family, maps encryption scope, coordinates rapid containment, preserves decryption artifacts, and manages communication.',
    longDescription:
      'Purpose-built for ransomware incidents — the highest-stakes scenario in modern security operations. This agent: (1) Identifies the ransomware family and variant from ransom notes, file extensions, and binary signatures, (2) Maps the encryption blast radius across file shares, endpoints, and cloud storage, (3) Coordinates emergency containment — network segmentation, SMB block, service account disables, (4) Preserves potential decryption keys from memory and registry, (5) Checks for known decryptors (ID Ransomware, No More Ransom), (6) Manages stakeholder communication (SOC, CISO, Legal, PR, insurance), (7) Tracks recovery progress across affected systems. All actions are fully audited for regulatory and insurance reporting.',
    tags: ['ransomware', 'encryption', 'containment', 'decryption', 'blast-radius', 'crisis'],
    linkedSkills: [
      { id: 'identify-ransomware-family', name: 'Identify Ransomware Family', type: 'standard' },
      { id: 'map-encryption-scope', name: 'Map Encryption Blast Radius', type: 'standard' },
      { id: 'emergency-network-segment', name: 'Emergency Network Segmentation', type: 'connector_skill', connector: 'palo-alto-ngfw-v1' },
      { id: 'block-smb-lateral', name: 'Block SMB Lateral Movement', type: 'connector_skill', connector: 'palo-alto-ngfw-v1' },
      { id: 'mass-isolate-endpoints', name: 'Mass Endpoint Isolation', type: 'connector_skill', connector: 'crowdstrike-v1' },
      { id: 'disable-service-accounts', name: 'Disable Service Accounts', type: 'connector_skill', connector: 'entra-id-v1' },
      { id: 'preserve-decryption-keys', name: 'Preserve Decryption Artifacts', type: 'standard' },
      { id: 'check-known-decryptors', name: 'Check Known Decryptors', type: 'standard' },
      { id: 'stakeholder-notification', name: 'Stakeholder Crisis Notification', type: 'connector_skill', connector: 'slack-v1' },
      { id: 'create-crisis-ticket', name: 'Create Crisis Ticket (SEV-1)', type: 'connector_skill', connector: 'servicenow-v1' },
      { id: 'generate-ransomware-report', name: 'Generate Ransomware Incident Report', type: 'standard' },
    ],
    linkedWorkflows: [
      { id: 'wf-ransomware-contain', name: 'Ransomware Containment', description: 'Identify → Segment Network → Isolate Endpoints → Disable Accounts → Preserve Keys' },
      { id: 'wf-ransomware-assess', name: 'Ransomware Impact Assessment', description: 'Map Scope → Identify Family → Check Decryptors → Recovery Plan' },
      { id: 'wf-ransomware-communicate', name: 'Crisis Communication', description: 'Notify SOC → Notify CISO → Notify Legal → Track Updates' },
    ],
    linkedConnectors: ['crowdstrike-v1', 'palo-alto-ngfw-v1', 'entra-id-v1', 'splunk-v1', 'servicenow-v1', 'slack-v1', 'teams-v1'],
    policyConfig: {
      approvalRequired: ['mass-isolate-endpoints', 'emergency-network-segment', 'disable-service-accounts'],
      autoApproved: ['identify-ransomware-family', 'map-encryption-scope', 'stakeholder-notification'],
      escalationChain: ['SOC Lead', 'CISO', 'Legal', 'CEO'],
      allowedActions: ['identify', 'map-scope', 'isolate', 'segment', 'disable-accounts', 'notify', 'report'],
      blockedActions: ['pay-ransom', 'contact-attacker', 'delete-evidence', 'restore-without-scan'],
    },
    guardrails: [
      { id: 'gr-never-pay', severity: 'critical', rule: 'Never engage with ransom demands or facilitate payment — escalate to Legal and CISO' },
      { id: 'gr-preserve-keys', severity: 'critical', rule: 'Always preserve potential decryption artifacts before any remediation actions' },
      { id: 'gr-no-restore-without-scan', severity: 'critical', rule: 'Never restore from backups without confirming backups are clean' },
      { id: 'gr-mass-isolate-approval', severity: 'critical', rule: 'Mass endpoint isolation requires CISO-level approval' },
    ],
    evals: [
      { name: 'Ransomware Family Identification', type: 'functional', description: 'Tests against 10 major ransomware families — target 100% identification' },
      { name: 'Containment Speed', type: 'functional', description: 'Measures time from detection to containment initiation (target: <5 min)' },
      { name: 'No Ransom Payment Gate', type: 'guardrail', description: 'Simulates pressure to pay — verifies agent refuses and escalates' },
    ],
    estimatedSetupMinutes: 45,
    requiredConnectors: ['crowdstrike-v1 OR sentinelone-v1', 'palo-alto-ngfw-v1', 'entra-id-v1', 'splunk-v1 OR sentinel-v1', 'servicenow-v1', 'slack-v1 OR teams-v1'],
  },

  /* ══════════════════════════════════════════════════════════════════
   *  VULNERABILITY MANAGEMENT
   * ══════════════════════════════════════════════════════════════════ */

  {
    id: 'vuln-management-agent',
    name: 'Vulnerability Management Analyst',
    version: '1.0.0',
    category: 'vuln-management',
    expertise: 'intermediate',
    tier: 'premium',
    status: 'stable',
    icon: '🎯',
    vendor: 'Agentlets',
    description:
      'End-to-end vulnerability management agent. Ingests scan results, prioritizes by risk (CVSS + context), tracks remediation SLAs, and generates compliance reports.',
    longDescription:
      'Automates the vulnerability management lifecycle. Ingests scan results from Tenable, Qualys, or Rapid7; deduplicates and normalizes findings; enriches with CISA KEV, EPSS scores, and asset criticality context; prioritizes using a risk-based model (not just CVSS); assigns remediation owners and SLA deadlines; tracks patching progress; generates management dashboards and compliance reports. Supports exception workflows for vulnerabilities that cannot be immediately patched.',
    tags: ['vulnerability', 'scanning', 'cvss', 'epss', 'patching', 'sla', 'remediation'],
    linkedSkills: [
      { id: 'ingest-scan-results', name: 'Ingest Vulnerability Scan Results', type: 'connector_skill', connector: 'tenable-v1' },
      { id: 'enrich-cisa-kev', name: 'Enrich with CISA KEV Status', type: 'standard' },
      { id: 'calculate-risk-score', name: 'Calculate Contextual Risk Score', type: 'standard' },
      { id: 'assign-remediation-owner', name: 'Assign Remediation Owner', type: 'connector_skill', connector: 'servicenow-v1' },
      { id: 'track-sla-compliance', name: 'Track SLA Compliance', type: 'standard' },
      { id: 'generate-vuln-report', name: 'Generate Vulnerability Report', type: 'standard' },
      { id: 'escalate-overdue', name: 'Escalate Overdue Remediations', type: 'connector_skill', connector: 'slack-v1' },
    ],
    linkedWorkflows: [
      { id: 'wf-vuln-lifecycle', name: 'Vulnerability Lifecycle', description: 'Scan → Normalize → Enrich → Prioritize → Assign → Track → Report' },
    ],
    linkedConnectors: ['tenable-v1', 'qualys-v1', 'servicenow-v1', 'slack-v1'],
    policyConfig: {
      slaByRisk: { critical: 7, high: 30, medium: 90, low: 180 },
      autoAssignByAssetOwner: true,
      escalateAfterSlaDays: 3,
      allowedActions: ['ingest', 'enrich', 'assign', 'track', 'report', 'escalate'],
      blockedActions: ['auto-patch', 'modify-scan-config', 'waive-vulnerability'],
    },
    guardrails: [
      { id: 'gr-no-auto-patch', severity: 'critical', rule: 'Never auto-apply patches — only assign and track remediation' },
      { id: 'gr-kev-escalation', severity: 'high', rule: 'CISA KEV vulnerabilities must be escalated to Security Lead within 24 hours' },
      { id: 'gr-exception-approval', severity: 'high', rule: 'Vulnerability exceptions/waivers require Risk Owner + CISO approval' },
    ],
    evals: [
      { name: 'Risk Score Accuracy', type: 'functional', description: 'Validates contextual risk scoring against expert-labeled test set' },
      { name: 'SLA Tracking', type: 'functional', description: 'Simulates overdue remediation — verifies escalation triggers' },
      { name: 'No Auto-Patch', type: 'guardrail', description: 'Attempts auto-patching — verifies block' },
    ],
    estimatedSetupMinutes: 20,
    requiredConnectors: ['tenable-v1 OR qualys-v1', 'servicenow-v1 OR jira-v1'],
  },

  /* ══════════════════════════════════════════════════════════════════
   *  CLOUD SECURITY
   * ══════════════════════════════════════════════════════════════════ */

  {
    id: 'cloud-security-monitor-agent',
    name: 'Cloud Security Posture Monitor',
    version: '1.0.0',
    category: 'cloud-security',
    expertise: 'intermediate',
    tier: 'premium',
    status: 'stable',
    icon: '☁️',
    vendor: 'Agentlets',
    description:
      'Monitors cloud environments (AWS, Azure, GCP) for misconfigurations, exposed resources, excessive permissions, and suspicious activity. Maps to CIS Benchmarks.',
    longDescription:
      'Continuous cloud security posture monitoring across multi-cloud environments. This agent: (1) Scans for CIS Benchmark violations across compute, storage, networking, and IAM, (2) Detects publicly exposed S3 buckets, storage accounts, and databases, (3) Identifies over-privileged IAM roles and dormant credentials, (4) Monitors for suspicious cloud activity (unusual API calls, impossible travel, privilege escalation), (5) Tracks resource inventory changes for shadow IT detection, (6) Generates compliance dashboards mapped to CIS, SOC 2, and cloud provider best practices.',
    tags: ['cloud', 'aws', 'azure', 'gcp', 'cspm', 'cis-benchmark', 'misconfiguration'],
    linkedSkills: [
      { id: 'scan-aws-config', name: 'Scan AWS Configuration', type: 'connector_skill', connector: 'aws-security-hub-v1' },
      { id: 'scan-azure-config', name: 'Scan Azure Configuration', type: 'connector_skill', connector: 'prisma-cloud-v1' },
      { id: 'detect-public-exposure', name: 'Detect Publicly Exposed Resources', type: 'standard' },
      { id: 'audit-iam-permissions', name: 'Audit IAM Permissions', type: 'standard' },
      { id: 'detect-suspicious-cloud-activity', name: 'Detect Suspicious Cloud Activity', type: 'connector_skill', connector: 'aws-security-hub-v1' },
      { id: 'map-cis-compliance', name: 'Map to CIS Benchmarks', type: 'standard' },
      { id: 'generate-cloud-report', name: 'Generate Cloud Security Report', type: 'standard' },
      { id: 'alert-critical-misconfig', name: 'Alert Critical Misconfiguration', type: 'connector_skill', connector: 'slack-v1' },
    ],
    linkedWorkflows: [
      { id: 'wf-cloud-posture-scan', name: 'Cloud Posture Scan', description: 'Scan All Accounts → Check CIS → Detect Exposure → Report → Alert' },
    ],
    linkedConnectors: ['aws-security-hub-v1', 'prisma-cloud-v1', 'slack-v1', 'servicenow-v1'],
    policyConfig: {
      scanIntervalHours: 6,
      criticalMisconfigAutoAlert: true,
      allowedActions: ['scan', 'audit', 'detect', 'report', 'alert'],
      blockedActions: ['modify-iam', 'delete-resources', 'change-security-groups', 'modify-network'],
    },
    guardrails: [
      { id: 'gr-no-infra-changes', severity: 'critical', rule: 'Never modify cloud infrastructure — only detect and report' },
      { id: 'gr-public-exposure-immediate', severity: 'critical', rule: 'Publicly exposed databases or secrets must trigger immediate alert' },
      { id: 'gr-scope-boundaries', severity: 'high', rule: 'Only scan accounts explicitly authorized in configuration' },
    ],
    evals: [
      { name: 'CIS Benchmark Coverage', type: 'functional', description: 'Validates detection of top 20 CIS violations — target >95%' },
      { name: 'Public Exposure Detection', type: 'functional', description: 'Tests with intentionally exposed resources — target 100% detection' },
      { name: 'No Infra Changes', type: 'guardrail', description: 'Attempts to remediate finding — verifies block' },
    ],
    estimatedSetupMinutes: 25,
    requiredConnectors: ['aws-security-hub-v1 OR prisma-cloud-v1', 'slack-v1'],
  },

  /* ══════════════════════════════════════════════════════════════════
   *  COMPLIANCE & AUDIT
   * ══════════════════════════════════════════════════════════════════ */

  {
    id: 'compliance-monitor-agent',
    name: 'Compliance & Audit Monitor',
    version: '1.0.0',
    category: 'compliance-audit',
    expertise: 'intermediate',
    tier: 'premium',
    status: 'stable',
    icon: '📋',
    vendor: 'Agentlets',
    description:
      'Continuous compliance monitoring agent. Tracks control effectiveness across SOC 2, ISO 27001, HIPAA, and PCI-DSS. Collects evidence, flags gaps, and generates audit-ready reports.',
    longDescription:
      'Automates compliance evidence collection and gap analysis. Maps your security controls to multiple compliance frameworks simultaneously (SOC 2 Type II, ISO 27001:2022, HIPAA Security Rule, PCI-DSS v4.0). Continuously monitors control effectiveness by verifying: (1) Access reviews completed on schedule, (2) Vulnerability scan coverage, (3) Incident response procedures tested, (4) Change management processes followed, (5) Encryption at rest and in transit enforced, (6) Backup and recovery tested. Generates framework-specific reports with evidence packages ready for auditor review.',
    tags: ['compliance', 'soc2', 'iso27001', 'hipaa', 'pci-dss', 'audit', 'evidence'],
    linkedSkills: [
      { id: 'map-controls-to-frameworks', name: 'Map Controls to Frameworks', type: 'standard' },
      { id: 'collect-compliance-evidence', name: 'Collect Compliance Evidence', type: 'standard' },
      { id: 'verify-access-reviews', name: 'Verify Access Reviews', type: 'connector_skill', connector: 'entra-id-v1' },
      { id: 'verify-scan-coverage', name: 'Verify Scan Coverage', type: 'connector_skill', connector: 'tenable-v1' },
      { id: 'verify-ir-procedures', name: 'Verify IR Procedures Tested', type: 'connector_skill', connector: 'servicenow-v1' },
      { id: 'generate-compliance-report', name: 'Generate Compliance Report', type: 'standard' },
      { id: 'flag-compliance-gaps', name: 'Flag Compliance Gaps', type: 'standard' },
      { id: 'notify-compliance-gap', name: 'Notify Compliance Gap', type: 'connector_skill', connector: 'slack-v1' },
    ],
    linkedWorkflows: [
      { id: 'wf-compliance-cycle', name: 'Compliance Monitoring Cycle', description: 'Collect Evidence → Verify Controls → Map to Frameworks → Gap Analysis → Report' },
    ],
    linkedConnectors: ['entra-id-v1', 'tenable-v1', 'servicenow-v1', 'splunk-v1', 'slack-v1'],
    policyConfig: {
      frameworks: ['soc2-type2', 'iso-27001', 'hipaa', 'pci-dss-v4'],
      evidenceRetentionDays: 365,
      gapEscalationDays: 7,
      allowedActions: ['collect-evidence', 'verify-controls', 'report', 'flag-gaps', 'notify'],
      blockedActions: ['modify-controls', 'waive-requirements', 'grant-exceptions'],
    },
    guardrails: [
      { id: 'gr-no-evidence-modification', severity: 'critical', rule: 'Never modify or fabricate compliance evidence' },
      { id: 'gr-no-auto-exceptions', severity: 'critical', rule: 'Compliance exceptions require GRC team approval — never auto-grant' },
      { id: 'gr-evidence-integrity', severity: 'high', rule: 'All evidence must include collection timestamp and source verification' },
    ],
    evals: [
      { name: 'Framework Mapping Accuracy', type: 'functional', description: 'Validates control-to-framework mapping against reference dataset' },
      { name: 'Evidence Collection Completeness', type: 'functional', description: 'Checks all required evidence types are collected for SOC 2 Type II' },
      { name: 'No Evidence Fabrication', type: 'guardrail', description: 'Verifies agent cannot generate synthetic evidence' },
    ],
    estimatedSetupMinutes: 30,
    requiredConnectors: ['entra-id-v1 OR okta-v1', 'tenable-v1 OR qualys-v1', 'servicenow-v1'],
  },

  /* ══════════════════════════════════════════════════════════════════
   *  IDENTITY & ACCESS SECURITY
   * ══════════════════════════════════════════════════════════════════ */

  {
    id: 'identity-threat-agent',
    name: 'Identity Threat Detection Agent',
    version: '1.0.0',
    category: 'identity-security',
    expertise: 'intermediate',
    tier: 'premium',
    status: 'stable',
    icon: '🔑',
    vendor: 'Agentlets',
    description:
      'Monitors identity infrastructure for threats. Detects impossible travel, privilege escalation, dormant account abuse, MFA bypass, and lateral movement via credential reuse.',
    longDescription:
      'Specialized agent for identity-based threats — the #1 attack vector in modern breaches. Monitors your identity provider (Entra ID, Okta, Google Workspace) for: (1) Impossible travel — login from geographically impossible locations, (2) Privilege escalation — unexpected role/group changes, (3) Dormant account activation — accounts unused for 90+ days suddenly active, (4) MFA bypass patterns — MFA fatigue attacks, token theft, (5) Credential stuffing — high-volume failed auth attempts, (6) Service account anomalies — service accounts used interactively. Integrates with CASB for shadow IT detection via SSO analysis.',
    tags: ['identity', 'iam', 'impossible-travel', 'mfa', 'privilege-escalation', 'credential'],
    linkedSkills: [
      { id: 'monitor-auth-logs', name: 'Monitor Authentication Logs', type: 'connector_skill', connector: 'entra-id-v1' },
      { id: 'detect-impossible-travel', name: 'Detect Impossible Travel', type: 'standard' },
      { id: 'detect-priv-escalation', name: 'Detect Privilege Escalation', type: 'connector_skill', connector: 'entra-id-v1' },
      { id: 'detect-dormant-activation', name: 'Detect Dormant Account Activation', type: 'standard' },
      { id: 'detect-mfa-bypass', name: 'Detect MFA Bypass Patterns', type: 'standard' },
      { id: 'detect-credential-stuffing', name: 'Detect Credential Stuffing', type: 'standard' },
      { id: 'disable-compromised-account', name: 'Disable Compromised Account', type: 'connector_skill', connector: 'entra-id-v1' },
      { id: 'force-mfa-reenroll', name: 'Force MFA Re-enrollment', type: 'connector_skill', connector: 'entra-id-v1' },
      { id: 'notify-identity-alert', name: 'Notify Identity Alert', type: 'connector_skill', connector: 'slack-v1' },
    ],
    linkedWorkflows: [
      { id: 'wf-identity-threat-response', name: 'Identity Threat Response', description: 'Detect → Validate → Contain (Disable/Reset) → Investigate → Notify' },
    ],
    linkedConnectors: ['entra-id-v1', 'okta-v1', 'splunk-v1', 'servicenow-v1', 'slack-v1'],
    policyConfig: {
      impossibleTravelThresholdKmph: 900,
      dormantThresholdDays: 90,
      mfaFatigueThreshold: 5,
      credentialStuffingThreshold: 50,
      approvalRequired: ['disable-compromised-account'],
      autoApproved: ['force-mfa-reenroll', 'notify-identity-alert'],
      allowedActions: ['detect', 'disable-account', 'reset-mfa', 'notify', 'create-ticket'],
      blockedActions: ['delete-account', 'modify-mfa-policy', 'grant-roles'],
    },
    guardrails: [
      { id: 'gr-no-account-delete', severity: 'critical', rule: 'Never delete accounts — only disable for containment' },
      { id: 'gr-executive-escalation', severity: 'high', rule: 'Executive account actions require CISO approval' },
      { id: 'gr-service-account-caution', severity: 'high', rule: 'Service account disable requires impact assessment before execution' },
    ],
    evals: [
      { name: 'Impossible Travel Detection', type: 'functional', description: 'Tests with synthetic auth logs showing impossible travel — target 100% detection' },
      { name: 'MFA Fatigue Detection', type: 'functional', description: 'Simulates MFA fatigue attack pattern — verifies detection and alerting' },
      { name: 'No Account Deletion', type: 'guardrail', description: 'Attempts account deletion — verifies block' },
    ],
    estimatedSetupMinutes: 20,
    requiredConnectors: ['entra-id-v1 OR okta-v1', 'splunk-v1 OR sentinel-v1'],
  },

  /* ══════════════════════════════════════════════════════════════════
   *  NETWORK SECURITY
   * ══════════════════════════════════════════════════════════════════ */

  {
    id: 'network-threat-agent',
    name: 'Network Threat Detection Agent',
    version: '1.0.0',
    category: 'network-security',
    expertise: 'advanced',
    tier: 'enterprise',
    status: 'stable',
    icon: '🌐',
    vendor: 'Agentlets',
    description:
      'Advanced network security agent. Monitors for C2 communication, DNS tunneling, data exfiltration, lateral movement, and network-based attacks using firewall, proxy, and DNS logs.',
    longDescription:
      'Deep network security monitoring for sophisticated threats that bypass endpoint controls. Analyzes: (1) DNS query patterns — detects DGA domains, DNS tunneling, and fast-flux networks, (2) Network flows — identifies beaconing patterns, low-and-slow C2, and unusual data transfer volumes, (3) Proxy logs — detects evasion techniques, category abuse, and unauthorized tunneling, (4) Firewall logs — identifies port scanning, ACL violations, and network reconnaissance, (5) East-west traffic — detects lateral movement, privilege escalation via network, and unauthorized service access. Supports automated firewall block rules for confirmed C2 infrastructure (with approval).',
    tags: ['network', 'c2', 'dns-tunneling', 'exfiltration', 'lateral-movement', 'beaconing'],
    linkedSkills: [
      { id: 'analyze-dns-patterns', name: 'Analyze DNS Query Patterns', type: 'connector_skill', connector: 'splunk-v1' },
      { id: 'detect-dga-domains', name: 'Detect DGA Domains', type: 'standard' },
      { id: 'detect-beaconing', name: 'Detect C2 Beaconing', type: 'standard' },
      { id: 'detect-dns-tunneling', name: 'Detect DNS Tunneling', type: 'standard' },
      { id: 'detect-data-exfiltration', name: 'Detect Data Exfiltration', type: 'standard' },
      { id: 'analyze-east-west-traffic', name: 'Analyze East-West Traffic', type: 'connector_skill', connector: 'palo-alto-ngfw-v1' },
      { id: 'block-c2-infrastructure', name: 'Block C2 Infrastructure', type: 'connector_skill', connector: 'palo-alto-ngfw-v1' },
      { id: 'generate-network-report', name: 'Generate Network Threat Report', type: 'standard' },
    ],
    linkedWorkflows: [
      { id: 'wf-network-threat-investigation', name: 'Network Threat Investigation', description: 'Detect Pattern → Validate → Correlate → Block (with approval) → Report' },
    ],
    linkedConnectors: ['splunk-v1', 'palo-alto-ngfw-v1', 'netskope-v1', 'crowdstrike-v1'],
    policyConfig: {
      approvalRequired: ['block-c2-infrastructure'],
      autoApproved: ['analyze-dns-patterns', 'detect-dga-domains', 'detect-beaconing'],
      beaconingThresholdMinutes: 60,
      exfiltrationThresholdMB: 500,
      allowedActions: ['analyze', 'detect', 'block-ip', 'report', 'notify'],
      blockedActions: ['modify-firewall-policy', 'change-network-topology', 'disable-monitoring'],
    },
    guardrails: [
      { id: 'gr-no-policy-changes', severity: 'critical', rule: 'Never modify firewall policies — only add specific block rules with approval' },
      { id: 'gr-validate-c2', severity: 'high', rule: 'Must have 2+ corroborating signals before requesting C2 block' },
      { id: 'gr-no-production-impact', severity: 'high', rule: 'Verify IP is not a critical business service before blocking' },
    ],
    evals: [
      { name: 'DGA Detection Rate', type: 'functional', description: 'Tests against 100 DGA domains + 100 legitimate — target >95% accuracy' },
      { name: 'Beaconing Detection', type: 'functional', description: 'Simulates C2 beaconing at various intervals — target >90% detection' },
      { name: 'C2 Block Approval Gate', type: 'guardrail', description: 'Attempts auto-block without approval — verifies rejection' },
    ],
    estimatedSetupMinutes: 30,
    requiredConnectors: ['splunk-v1 OR sentinel-v1', 'palo-alto-ngfw-v1'],
  },

  /* ══════════════════════════════════════════════════════════════════
   *  CASB / SASE SECURITY
   * ══════════════════════════════════════════════════════════════════ */

  {
    id: 'casb-dlp-agent',
    name: 'CASB & Data Loss Prevention Agent',
    version: '1.0.0',
    category: 'cloud-security',
    expertise: 'intermediate',
    tier: 'premium',
    status: 'stable',
    icon: '🔒',
    vendor: 'Agentlets',
    description:
      'Monitors cloud application usage and data loss indicators via CASB (Netskope/Zscaler). Detects shadow IT, unauthorized file sharing, and sensitive data exposure.',
    longDescription:
      'Leverages CASB integration to provide visibility into cloud application usage and data protection. This agent: (1) Discovers shadow IT by analyzing cloud application traffic patterns, (2) Monitors file sharing for sensitive data exposure (PII, PHI, financial data), (3) Detects policy violations in sanctioned SaaS apps, (4) Identifies risky user behavior (bulk downloads, unusual sharing patterns), (5) Monitors DLP policy violations and generates incident reports, (6) Integrates with identity providers to correlate cloud activity with user risk profiles.',
    tags: ['casb', 'dlp', 'shadow-it', 'data-protection', 'saas', 'netskope', 'zscaler'],
    linkedSkills: [
      { id: 'discover-shadow-apps', name: 'Discover Shadow IT Applications', type: 'connector_skill', connector: 'netskope-v1' },
      { id: 'monitor-file-sharing', name: 'Monitor File Sharing Activity', type: 'connector_skill', connector: 'netskope-v1' },
      { id: 'detect-dlp-violations', name: 'Detect DLP Policy Violations', type: 'connector_skill', connector: 'netskope-v1' },
      { id: 'analyze-user-behavior', name: 'Analyze User Behavior Risk', type: 'standard' },
      { id: 'get-casb-alerts', name: 'Get CASB Alerts', type: 'connector_skill', connector: 'netskope-v1' },
      { id: 'quarantine-file-casb', name: 'Quarantine Sensitive File', type: 'connector_skill', connector: 'netskope-v1' },
      { id: 'generate-dlp-report', name: 'Generate DLP Incident Report', type: 'standard' },
    ],
    linkedWorkflows: [
      { id: 'wf-dlp-response', name: 'DLP Incident Response', description: 'Detect Violation → Classify Data → Quarantine → Notify Owner → Report' },
    ],
    linkedConnectors: ['netskope-v1', 'zscaler-v1', 'entra-id-v1', 'slack-v1', 'servicenow-v1'],
    policyConfig: {
      dlpSensitivityLevels: ['public', 'internal', 'confidential', 'restricted'],
      autoQuarantineAboveLevel: 'restricted',
      shadowItRiskThreshold: 'high',
      allowedActions: ['discover', 'monitor', 'detect', 'quarantine-file', 'report', 'notify'],
      blockedActions: ['block-application', 'modify-dlp-policy', 'delete-files'],
    },
    guardrails: [
      { id: 'gr-no-app-blocking', severity: 'critical', rule: 'Never block cloud applications — only report and escalate' },
      { id: 'gr-quarantine-not-delete', severity: 'critical', rule: 'Quarantine sensitive files — never delete them' },
      { id: 'gr-user-notification', severity: 'high', rule: 'Always notify the user before quarantining their files' },
    ],
    evals: [
      { name: 'Shadow IT Detection', type: 'functional', description: 'Tests against 20 unsanctioned app patterns — target >90% detection' },
      { name: 'DLP Violation Detection', type: 'functional', description: 'Injects test PII documents — verifies detection and quarantine' },
    ],
    estimatedSetupMinutes: 20,
    requiredConnectors: ['netskope-v1 OR zscaler-v1', 'entra-id-v1 OR okta-v1'],
  },

];

/* ─── HELPER FUNCTIONS ──────────────────────────────────────────────── */

export function getAgentsByCategory(categoryId) {
  return MARKETPLACE_AGENTS.filter(a => a.category === categoryId);
}

export function getAgentsByExpertise(expertiseId) {
  return MARKETPLACE_AGENTS.filter(a => a.expertise === expertiseId);
}

export function getAgentsByTier(tier) {
  return MARKETPLACE_AGENTS.filter(a => a.tier === tier);
}

export function getAgentById(agentId) {
  return MARKETPLACE_AGENTS.find(a => a.id === agentId);
}

export function getAgentRequiredConnectors(agentId) {
  const agent = getAgentById(agentId);
  return agent ? agent.linkedConnectors : [];
}

export function getAgentStats() {
  const byCategory = {};
  const byExpertise = {};
  const byTier = {};
  MARKETPLACE_AGENTS.forEach(a => {
    byCategory[a.category] = (byCategory[a.category] || 0) + 1;
    byExpertise[a.expertise] = (byExpertise[a.expertise] || 0) + 1;
    byTier[a.tier] = (byTier[a.tier] || 0) + 1;
  });
  return { total: MARKETPLACE_AGENTS.length, byCategory, byExpertise, byTier };
}
